// LocalPickr: the customer-facing brand that replaced "Offlico Find" (find.offlico.com)
// on 25 Oct 2026. Offlico (www.offlico.com) is now only the software for
// businesses; LocalPickr (localpickr.com) is where customers find, request and
// manage local mobile professionals. Its professional search is the home page:
// localpickr.com/?service=<slug>&where=<place> shows the results there (Rithik,
// 25 Oct 2026: "we don't need the search page because it's in the home page").
// The old search page (/search/) and find.localpickr.com redirect to it.
// Shared by the main SPA, the find.localpickr.com app and the build scripts:
// keep it free of React and browser globals.

export const OFFLICO_ORIGIN = "https://www.offlico.com";
export const LOCALPICKR_ORIGIN = "https://localpickr.com";
export const LOCALPICKR_FIND_ORIGIN = "https://find.localpickr.com"; // redirect-only: 301 to LOCALPICKR_SEARCH_URL
// The search: the home page. /search/ was its own page until 25 Oct 2026 and now
// 301s here with its query string (Render rules, seo-ops/scripts/site_routes.py).
export const LOCALPICKR_SEARCH_PATH = "/";
export const LOCALPICKR_SEARCH_URL = "https://localpickr.com/";
export const LOCALPICKR_HOSTNAMES = ["localpickr.com", "www.localpickr.com"];
// The former Render routes permanently redirected /blog URLs to Offlico. A
// distinct navigation URL bypasses those redirects in browsers that cached
// them, while clean /blog/ URLs remain the SEO canonicals and still work.
export const LOCALPICKR_GUIDES_NAV_QUERY = "?lp=20261027";
export const LOCALPICKR_GUIDES_HREF = "/blog/" + LOCALPICKR_GUIDES_NAV_QUERY;
export const localPickrGuideHref = (slug) => `/blog/${slug}/${LOCALPICKR_GUIDES_NAV_QUERY}`;
// Offlico's sign-in for professionals: LocalPickr's "For businesses" button.
export const OFFLICO_BUSINESS_LOGIN_URL = OFFLICO_ORIGIN + "/signin?redirect=/app";

// Gift cards (docs/GIFT-CARDS.md). Off until the backend sells them: the
// "Send a gift card" links and the /gift-cards/ pages show only when true.
export const GIFT_CARDS_ENABLED = true;
export const LOCALPICKR_GIFT_CARDS_PATH = "/gift-cards/";
export const LOCALPICKR_GIFT_CARD_BALANCE_PATH = "/gift-cards/balance/";
export const LOCALPICKR_GIFT_CARD_TERMS_PATH = "/gift-cards/terms/";
// Designs: the same keys in the backend and the email images
// (/localpickr/gift-cards/<key>.png).
export const GIFT_CARD_DESIGNS = [
  { key: "lp-indigo", name: "LocalPickr", group: "LocalPickr" },
  { key: "lp-teal", name: "Fresh", group: "LocalPickr" },
  { key: "lp-sunset", name: "Sunset", group: "LocalPickr" },
  { key: "birthday", name: "Happy birthday", group: "Occasions" },
  { key: "thank-you", name: "Thank you", group: "Occasions" },
  { key: "festive", name: "Season's greetings", group: "Occasions" },
];
export const LOCALPICKR_FIND_HOSTNAME = "find.localpickr.com";
// Hosts that only redirect now (Render redirect-only static sites).
export const LEGACY_BUSINESS_HOSTNAME = "business.offlico.com";
export const LEGACY_FIND_HOSTNAME = "find.offlico.com";

export const LOCALPICKR = {
  name: "LocalPickr",
  operatorLine: "LocalPickr is a service of Offlico Ltd.",
  tagline: "Find local mobile professionals who come to you.",
  description:
    "Find and book local mobile cleaners, gardeners, hairdressers, chiropodists and ear wax removal specialists who come to your home.",
  // LocalPickr's own support address (26 Oct 2026): the Help page's email card, every
  // "Report this listing" link and the gift card terms.
  supportEmail: "support@localpickr.com",
  colors: {
    primary: "#3D52D5",
    primaryDark: "#2A3AAA",
    primaryLight: "#EEF1FD",
    primaryMid: "#A5B4FC",
    accent: "#FF6B4A",
    ink: "#14213D",
  },
  themeColor: "#3D52D5",
  logo: "/localpickr/localpickr-logo.svg",
  logoWhite: "/localpickr/localpickr-logo-white.svg",
  logoPng: "/localpickr/localpickr-logo.png",
  mark: "/localpickr/localpickr-mark.svg",
  favicon: "/localpickr/favicon.svg",
  faviconIco: "/localpickr/favicon.ico",
  favicon32: "/localpickr/favicon-32.png",
  appleTouchIcon: "/localpickr/apple-touch-icon.png",
  manifest: "/localpickr/site.webmanifest",
  ogImage: "/localpickr/og-image.png",
  // Logo artwork is 264 x 64 (house mark + wordmark; Rithik's house logo, 25 Oct 2026).
  logoAspectRatio: 264 / 64,
};

// The services LocalPickr covers, in the order customers see them. slug is the
// address segment used by /looking-for/<slug> and localpickr.com/?service=<slug>;
// the API accepts these slugs (backend directory_catalog.DIRECTORY_PROFESSION_SLUG_ALIASES).
export const LOCALPICKR_SERVICES = [
  { slug: "cleaner", key: "cleaning", label: "Cleaner", plural: "Cleaners" },
  { slug: "gardener", key: "gardening", label: "Gardener", plural: "Gardeners" },
  { slug: "hairdresser", key: "hairdressing", label: "Mobile hairdresser", plural: "Mobile hairdressers" },
  { slug: "chiropodist", key: "foot_care", label: "Chiropodist / foot care", plural: "Chiropodists" },
  { slug: "ear-wax-removal", key: "earwax", label: "Ear wax removal", plural: "Ear wax removal specialists" },
];

export function normalizeHostname(hostname) {
  return String(hostname || "").trim().toLowerCase().replace(/\.$/, "");
}

export function isLocalPickrHostname(hostname) {
  return LOCALPICKR_HOSTNAMES.includes(normalizeHostname(hostname));
}

export function isLocalPickrFindHostname(hostname) {
  return normalizeHostname(hostname) === LOCALPICKR_FIND_HOSTNAME;
}

export function isAnyLocalPickrHostname(hostname) {
  return isLocalPickrHostname(hostname) || isLocalPickrFindHostname(hostname);
}

// The LocalPickr search address for a service and a place (postcode, town or full
// address): the home page with ?service=<slug>&where=<place>, which shows the
// results. Unknown services are dropped.
export function localPickrSearchUrl({ service, where } = {}) {
  const url = new URL(LOCALPICKR_SEARCH_URL);
  const known = LOCALPICKR_SERVICES.find((item) => item.slug === service || item.key === service);
  if (known) url.searchParams.set("service", known.slug);
  const place = String(where || "").trim();
  if (place) url.searchParams.set("where", place);
  return url.href;
}
